/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState, useCallback, useEffect } from 'react';
import { ClientsService } from '@services/requests/housien-services/ClientsService';

export interface ClientOption {
  id: string;
  label: string;
  sublabel?: string;
  raw: any;
}

/**
 * Maps a raw client API record into a picker-friendly option.
 * Supports both flat (legacy) and nested (v2) client model shapes.
 *
 * @param c - The raw client record from the clients service.
 * @returns A normalized client option with id, label, and optional sublabel.
 */
function toClientOption(c: any): ClientOption {
  const id = c.metadata?.client_id ?? c.client_id ?? '';
  const name = c.personal_info?.full_name ?? c.full_name ?? '';
  const corp = c.identity?.corporate_name ?? c.corporate_name ?? '';
  const contacts = c.assignment?.contacts as Array<{ type: string; value: string }> | undefined;
  const phone = contacts?.find((ct) => ct.type === 'phone' || ct.type === 'whatsapp')?.value;
  const email = contacts?.find((ct) => ct.type === 'email')?.value;
  const sublabel = phone ?? email ?? c.phone ?? c.email ?? undefined;
  return {
    id,
    label: name || corp || id,
    sublabel,
    raw: c,
  };
}

/**
 * Loads and caches the agency's clients as picker options.
 *
 * @param agencyId - The agency whose clients to load; when absent the hook idles.
 * @returns The client options, a loading flag, and a reload function.
 */
export function useAgencyClients(agencyId?: string) {
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [isLoading, setIsLoading] = useState(Boolean(agencyId));

  const load = useCallback(async () => {
    if (!agencyId) return;
    setIsLoading(true);
    const { data } = await ClientsService.getAgencyClients(agencyId, 1, 50).catch(() => ({ data: [] }));
    const items = Array.isArray(data) ? data : [];
    setClients(items.map(toClientOption));
    setIsLoading(false);
  }, [agencyId]);

  useEffect(() => { load(); }, [load]);
  return { clients, isLoading, reload: load };
}
